import { useEffect, useMemo, useRef, useState } from "react";
import { StyledBox, StyledTextField } from "../../../styled-mui";
import "react-phone-input-2/lib/material.css";
import Autocomplete from "@mui/material/Autocomplete";
import { EMPTY_STRING } from "../../../constants/common";
import { useTranslation } from "react-i18next";
import { SvgChevronDown } from "../../svg/chevronDown";
import { useStorelocatorByLocation } from "../../../utils/hooks/swr/useStorelocatorByLocation";
import { StyledInputStore } from "./styled-inputStore";
import { useDispatch } from "react-redux";
import { STORES_LIST_GET_REQUEST_ACTION } from "../../../redux/actions/stores";
import { searchType } from "../../../types/store-types";
import { useStoreLocatorValue } from "../../../_foundation/context/store-locator-context";
import { Box, Typography } from "@mui/material";

interface StoreInputProps {
  onChange: (e: any, countryCode?: string) => void;
  onBlur: (e: any, countryCode?: string) => void;
  refStore: any;
  error: boolean;
  zipCode?: string;
  required?: boolean;
  dataLoadedOnDefault?: boolean;
  isFromClaim?: boolean;
  isLogin?: boolean;
  setRefStore?: any;
  isFromAppointmentForm?: boolean;
  selectedOption?: string;
  emptyDefaultValue?: boolean;
  cadStores?: any;
  type?: "secondary";
  showDistance?: boolean;
  storeName?: string;
  loadAllOnMount?: boolean;
  autoSelectShopId?: string;
}

const StoreInputContainer: React.FC<StoreInputProps> = ({
  onChange,
  onBlur,
  refStore,
  error = false,
  required = true,
  zipCode = null,
  dataLoadedOnDefault = true,
  isLogin,
  setRefStore,
  isFromClaim,
  isFromAppointmentForm,
  selectedOption,
  cadStores,
  emptyDefaultValue = false,
  type,
  showDistance,
  storeName,
  loadAllOnMount = false,
  autoSelectShopId = "",
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { storeLocator } = useStoreLocatorValue();

  const [storesSelect, setStoresSelect] = useState<any[]>([]);
  const [defaultStoresSelect, setDefaultStoresSelect] = useState<string | null>(null);
  const keepEmptyStoreOnLoad = useRef<boolean>(emptyDefaultValue);

  const zipForHook = loadAllOnMount
    ? autoSelectShopId
      ? null
      : zipCode && zipCode.length >= 5
      ? zipCode
      : null
    : dataLoadedOnDefault || (zipCode && zipCode.length >= 5)
    ? zipCode
    : "";

  const fetchedStores = useStorelocatorByLocation(zipForHook, isFromAppointmentForm, selectedOption);

  const isCadEligible = selectedOption === "CAD" && Array.isArray(cadStores) && cadStores.length > 0;

  useEffect(() => {
    if (loadAllOnMount) {
      setDefaultStoresSelect(null);
      getAllStoresList();
    }
  }, [loadAllOnMount, autoSelectShopId]);

  useEffect(() => {
    if (!autoSelectShopId) {
      if (refStore?._autoSelected) {
        setDefaultStoresSelect(null);
        setRefStore(null);
        onChange(null);
      }
      return;
    }
    if (storesSelect.length === 0) return;
    const normShopId = autoSelectShopId.replace(/^0+/, "");

    const matched = storesSelect.find((store) => {
      const normStoreId = String(store.id ?? "").replace(/^0+/, "");
      return normStoreId === normShopId;
    });
    if (matched) {
      const taggedStore = { ...matched, _autoSelected: true };
      setDefaultStoresSelect(matched.label);
      setRefStore(taggedStore);
      onChange(taggedStore);
    }
  }, [autoSelectShopId, storesSelect]);

  useEffect(() => {
    if (fetchedStores?.length > 0) {
      const storesArray = fetchedStores.map((store, index) => {
        let isVisio = false;
        try {
          const attrs = store.Attribute || store.attributes || [];
          if (Array.isArray(attrs)) {
            const vis = attrs.find((a: any) => (a.name || a.displayName || "").toLowerCase() === "isvisio");
            if (vis) {
              isVisio = vis.value === "1";
            }
          }
        } catch (e) {
          // ignore
        }

        return {
          id: store.storeName,
          label: store.name,
          postalCode: store.postalCode?.trim(),
          address: store.address,
          city: store.city,
          country: store.country,
          distance: store.distance != null && !isNaN(parseFloat(store.distance)) ? parseFloat(store.distance) : null,
          key: index,
          isVisio,
          Attribute: store.Attribute,
        };
      });

      let matchedStores: any = [];
      if (selectedOption === "CAD") {
        const homeAvailIds = new Set(cadStores.map((item) => item.idMagasin.replace(/^0+/, "")));
        matchedStores = storesArray.filter((store) => {
          const normalizedId = String(store.id).replace(/^0+/, "");
          return homeAvailIds.has(normalizedId) || homeAvailIds.has(store.id);
        });
      }

      setStoresSelect(matchedStores.length > 0 ? matchedStores : storesArray);

      if (keepEmptyStoreOnLoad.current) {
        keepEmptyStoreOnLoad.current = false;
      } else {
        const storesToUse = matchedStores.length > 0 ? matchedStores : storesArray;
        const selectedStore = storesArray.find(
          (store) => store.postalCode === storeLocator?.selectedStore?.postalCode?.trim()
        );

        if (!isFromAppointmentForm && selectedStore) {
          setDefaultStoresSelect(selectedStore.label);
          setRefStore(selectedStore);
        } else if (storesToUse.length > 0) {
          if (!loadAllOnMount) {
            setDefaultStoresSelect(storesToUse[0].label);
            setRefStore(storesToUse[0]);
            if (showDistance === true) {
              onChange(storesToUse[0]);
            }
          }
        } else if (storesArray.length > 0) {
          if (!loadAllOnMount) {
            setDefaultStoresSelect(storesArray[0].label);
            setRefStore(storesArray[0]);
            if (showDistance === true) {
              onChange(storesArray[0]);
            }
          }
        }
      }
    } else {
      if (isFromClaim) {
        getAllStoresList();
      }
      onChange(null);
      if (!autoSelectShopId) {
        setStoresSelect([]);
      }
    }
  }, [fetchedStores]);

  useEffect(() => {
    if (!isLogin && !autoSelectShopId) {
      const selectedStoreGuest = storesSelect.find((store) => store.id === storeName?.trim());
      if (selectedStoreGuest) {
        setDefaultStoresSelect(selectedStoreGuest.label);
        setRefStore(selectedStoreGuest);
      } else {
        setDefaultStoresSelect(null);
        setRefStore(null);
      }
    }
  }, [storesSelect, storeName, isLogin]);

  const handleChange = (newValue: any) => {
    if (newValue) {
      setDefaultStoresSelect(newValue.label);
      setRefStore(newValue);
      onChange(newValue);
    } else {
      setDefaultStoresSelect(null);
      setRefStore(null);
      onChange(null);
    }
  };

  const getAllStoresList = async () => {
    const parameters: any = {
      query: { allStores: true },
    };
    dispatch(
      STORES_LIST_GET_REQUEST_ACTION({
        parameters,
        type: searchType.allStores,
      })
    );
  };

  const storeValue = useMemo(
    () =>
      storesSelect.find((store) => store.label === refStore?.label) ??
      (defaultStoresSelect ? { label: defaultStoresSelect } : null),
    [storesSelect, refStore, defaultStoresSelect]
  );

  const isLockedByDoc = !!autoSelectShopId && !!refStore?._autoSelected;

  return (
    <StyledInputStore>
      <Autocomplete
        disabled={(!dataLoadedOnDefault && !loadAllOnMount && storesSelect.length === 0) || isLogin || isLockedByDoc}
        disablePortal
        id="combo-box-stores"
        options={storesSelect}
        getOptionLabel={(option) =>
          showDistance && option.distance != null
            ? `${option.label} - ${option.distance.toFixed(1)} km`
            : option.label || ""
        }
        popupIcon={<SvgChevronDown className="medium" />}
        sx={{ flex: 1, ".MuiAutocomplete-endAdornment": { top: "calc(50% - 20px);" } }}
        value={storeValue}
        onChange={(event, newValue) => {
          if (newValue) {
            const cleanValue = { ...newValue };
            delete cleanValue.distance;
            handleChange(cleanValue);
          } else {
            handleChange(null);
          }
        }}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            variant="standard"
            focused
            className={type === "secondary" ? "styled-v2" : "underline-gray"}
            required={required}
            fullWidth
            onBlur={(e) => onBlur(e)}
            label={
              <>
                {t("Forms.Labels.Store")}{" "}
                {required && (
                  <StyledBox component="span" color="primary.main">
                    *
                  </StyledBox>
                )}
              </>
            }
            error={error && !(!dataLoadedOnDefault && !loadAllOnMount && !fetchedStores)}
            helperText={
              error && !(!dataLoadedOnDefault && !loadAllOnMount && !fetchedStores)
                ? t("ErrorFormMessage.Store")
                : EMPTY_STRING
            }
          />
        )}
        renderOption={
          showDistance
            ? (props, option, { selected }) => (
                <li
                  {...props}
                  key={option.id}
                  style={{
                    backgroundColor: selected ? "#ecf8ff" : "inherit",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = selected ? "#ecf8ff" : "inherit")}>
                  <Box display="flex" justifyContent="space-between" width="100%">
                    <Box>
                      {option.label}
                      {option.city && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {option.city}
                        </Typography>
                      )}
                    </Box>
                    {option.distance != null && (
                      <Typography variant="caption" fontSize={12} mt={1} color="text.secondary">
                        {option.distance.toFixed(2)} km
                      </Typography>
                    )}
                  </Box>
                </li>
              )
            : undefined
        }
        ListboxProps={{ className: "autocomplete-listbox" }}
      />
    </StyledInputStore>
  );
};

export { StoreInputContainer };
